import Link from 'next/link'
import Head from 'next/head'
import { GetStaticProps } from 'next'
import { readItems } from '@directus/sdk'
import Header from '@/components/Header/Header'
import Footer from '@/components/Footer/Footer'
import directus from '@/lib/directus'

interface Custom404Props {
  headerData: any
  footerData: any
}

export default function Custom404({ headerData, footerData }: Custom404Props) {
	return (
		<>
			<Head>
				<title>
					Page Not Found | Best Surgeon Facelift London &amp; Neck Lift London
				</title>
				<meta
					name='description'
					content='The page you are looking for cannot be found.'
				/>
			</Head>

			<Header data={headerData} />

			{/* 404 Content */}
			<main className='bg-white flex flex-col items-center justify-center px-4 py-16 min-h-screen'>
				<div className='container mx-auto text-center'>
					<h1 className='text-4xl md:text-6xl font-freightBig leading-tight mb-8 '>
						Error #404
					</h1>
					<p className='mb-5 md:mb-10 text-lg'>THAT PAGE CANT BE FOUND</p>
					<h2 className='font-freightBig text-3xl md:text-5xl mb-6'>
						Looking for something?
					</h2>
					<p className='mb-8 text-lg'>Check out the following pages:</p>
					<div className='flex flex-col md:flex-row items-center justify-center gap-4'>
						<a
							href='/facelift-rhytidectomy/'
							className='inline-block px-8 py-5 rounded-full text-xs font-aileron font-bold uppercase tracking-wider transition-colors bg-black border border-white hover:bg-[#645e5e] text-white'
						>
							Facelift
						</a>
						<a
							href='/blepharoplasty-surgery-eyelid-lift/'
							className='inline-block px-8 py-5 rounded-full text-xs font-aileron font-bold uppercase tracking-wider transition-colors bg-black border border-white hover:bg-[#645e5e] text-white'
						>
							Blepharoplasty
						</a>
						<a
							href='/gallery/'
							className='inline-block px-8 py-5 rounded-full text-xs font-aileron font-bold uppercase tracking-wider transition-colors bg-black border border-white hover:bg-[#645e5e] text-white'
						>
							Gallery
						</a>
					</div>

					{/* Back to Home */}
					<div className='mt-12'>
						<a
							href='/'
							className='inline-block px-6 py-3 rounded-full text-sm font-medium transition-colors bg-gray-100 hover:bg-gray-200 text-gray-800'
						>
							← Back to Home
						</a>
					</div>
				</div>
			</main>

			<Footer data={footerData} />
		</>
	)
}

export const getStaticProps: GetStaticProps = async () => {
  try {
    // Get header data
    const headerResponse = await directus.request(
      readItems('header', {
        fields: [
          "phone_number.title",
          "phone_icon.id",
          "phone_icon.filename_disk",
          "navigation.menu_items.menu_items_id.label",
          "navigation.menu_items.menu_items_id.href",
          "navigation.menu_items.menu_items_id.submenu.label",
          "navigation.menu_items.menu_items_id.submenu.href",
          "logo_desktop.image_file.image.id",
          "logo_desktop.image_file.image.filename_download",
          "logo_desktop.alt",
        ],
      })
    );

    // Get footer data
    const footerResponse = await directus.request(
      readItems('footer', {
        fields: [
          "bottom_line.*",
          "bottom_line_menu.menu_items.menu_items_id.label",
          "bottom_line_menu.menu_items.menu_items_id.href",
          "address_text.text",
          "address_title.title",
          "copyright_footer_text.text",
          "contact_title.title",
          "contact_phone_title.title",
          "contact_email_title.title",
          "contact_email.title",
          "contact_phone.title",
          "map_link.href",
          "map_link.label",
          "google_rating_title.title",
          "google_rating_section.text",
          "our_services_title.title",
          "our_services_menu.menu_items.menu_items_id.label",
          "our_services_menu.menu_items.menu_items_id.href",
          "social_links.external_links_id.url",
          "social_links.external_links_id.target",
          "social_links.external_links_id.icon.id",
          "social_links.external_links_id.icon.filename_disk",
        ],
      })
    );

    return {
      props: {
        headerData: headerResponse?.[0] || {},
        footerData: footerResponse?.[0] || {},
      },
      revalidate: 3600, // Revalidate every hour
    };
  } catch (error) {
    console.error('Error fetching header/footer data:', error);
    return {
      props: {
        headerData: {},
        footerData: {},
      },
      revalidate: 3600,
    };
  }
}; 